import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, Overline } from "../components/motion";
import { CONTACT, PROGRAMS, COURSE_CATALOGUE } from "../data/content";
import { Phone, Mail, MapPin, Clock, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="text-xs tracking-[0.18em] uppercase text-muted-foreground">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);

const inputCls =
  "w-full bg-transparent border border-border rounded-sm px-4 py-3 text-sm text-navy placeholder:text-muted-foreground/60 focus:outline-none focus:border-accent transition-colors duration-300";

export default function ContactPage() {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", program: "", message: "" });
  const [sending, setSending] = useState(false);
  const [params] = useSearchParams();

  useEffect(() => {
    const course = params.get("course");
    if (course) setForm((f) => ({ ...f, program: course }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Thank you — your enquiry has been received. Our admissions team will be in touch shortly.");
      setForm({ first_name: "", last_name: "", email: "", phone: "", program: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="pt-20">
      <section className="py-20 md:py-28 bg-navy text-primary-foreground">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Overline light>Admissions & Contact</Overline>
          <h1 className="mt-4 font-serif text-5xl md:text-6xl tracking-tight leading-[1.05]">
            Start the <span className="italic text-gold">conversation</span>
          </h1>
          <p className="mt-6 max-w-2xl text-primary-foreground/60 leading-relaxed">
            Want to know more about a program? Have questions about dates or course content?
            Reach out and we'll get your questions answered.
          </p>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-7">
            <Reveal>
              <h2 className="font-serif text-3xl text-navy">Enquiry Form</h2>
              <form onSubmit={submit} data-testid="enquiry-form" className="mt-8 space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label="First Name *" testid="field-first-name">
                    <input required data-testid="input-first-name" className={inputCls} value={form.first_name} onChange={set("first_name")} placeholder="Jane" />
                  </Field>
                  <Field label="Last Name *" testid="field-last-name">
                    <input required data-testid="input-last-name" className={inputCls} value={form.last_name} onChange={set("last_name")} placeholder="Doe" />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label="Email *" testid="field-email">
                    <input required type="email" data-testid="input-email" className={inputCls} value={form.email} onChange={set("email")} placeholder="jane@example.com" />
                  </Field>
                  <Field label="Phone" testid="field-phone">
                    <input data-testid="input-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+1 (___) ___-____" />
                  </Field>
                </div>
                <Field label="Program of Interest" testid="field-program">
                  <select data-testid="input-program" className={inputCls} value={form.program} onChange={set("program")}>
                    <option value="">Select a program…</option>
                    <optgroup label="Programs">
                      {PROGRAMS.map((p) => (
                        <option key={p.id} value={p.title}>{p.title}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Individual Courses">
                      {COURSE_CATALOGUE.flatMap((g) => g.courses).map((c) => (
                        <option key={c.slug} value={c.name}>{c.name}</option>
                      ))}
                    </optgroup>
                    <option value="General Enquiry">General Enquiry</option>
                  </select>
                </Field>
                <Field label="Message" testid="field-message">
                  <textarea rows={5} data-testid="input-message" className={inputCls} value={form.message} onChange={set("message")} placeholder="Tell us about your goals…" />
                </Field>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="enquiry-submit-btn"
                  className="group inline-flex items-center gap-3 bg-navy text-primary-foreground px-8 py-3.5 rounded-sm text-sm tracking-wide transition-colors duration-300 hover:bg-accent disabled:opacity-60"
                >
                  {sending ? "Sending…" : "Submit Enquiry"}
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </form>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="bg-secondary/40 border border-border rounded-sm p-8 space-y-8 lg:sticky lg:top-32">
                <h3 className="font-serif text-2xl text-navy">Visit or Reach Us</h3>
                {[
                  [Phone, "College Phone", CONTACT.phone, "contact-phone"],
                  [Mail, "Email", CONTACT.email, "contact-email"],
                  [MapPin, "Address", CONTACT.address, "contact-address"],
                  [Clock, "College Hours", CONTACT.hours, "contact-hours"],
                ].map(([Icon, label, value, tid]) => (
                  <div key={tid} className="flex gap-4 items-start" data-testid={tid}>
                    <span className="mt-1 text-accent"><Icon size={18} /></span>
                    <div>
                      <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground">{label}</p>
                      <p className="mt-1.5 text-sm text-navy leading-relaxed">{value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
